import React from 'react'
import { graphql } from 'gatsby'
import Helmet from 'react-helmet'
import styled from 'styled-components'
import { Flex } from 'rebass'
import ContentBrandHero from '../components/ContentBrandHero'
import ContentBrandCtaBar from '../components/ContentBrandCtaBar'
import ContentBrandModels from '../components/ContentBrandModels'
import ContentBar from '../components/ContentBar'
import createModelNormaliser from '../../lib/model/model-to-vehicle-card-normaliser'
import normaliseUsedVehicleForVehicleCard from '../../lib/cars/used/used-vehicle-to-card-normaliser'
import createMeta from '../utils/meta-creator'
import FormTestDrive from '../components/ContentForms/TestDrive'
import ContentModal from '../components/ContentModal'
import { ModalSection } from '../components/ContentModalSection/components/ModalSectionStyled'
import UsedVehicleSelection from '../components/UsedVehicleSelection'
import htmlStripper from '../../lib/html-stripper'
import Layout from '../components/Layout'
import normaliseMake from '../utils/make-normaliser'

const FlexWrapper = styled(Flex)`
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.white};
`
class Model extends React.Component {
  constructor(props) {
    super(props)
    this.handleTestDriveEnqiryToggle =
      this.handleTestDriveEnqiryToggle.bind(this)
    this.state = {
      isTestDriveEnquiryOpen: false
    }
  }

  handleTestDriveEnqiryToggle() {
    this.setState(({ isTestDriveEnquiryOpen }) => ({
      isTestDriveEnquiryOpen: !isTestDriveEnquiryOpen
    }))
  }

  render() {
    const { data, pageContext } = this.props
    const usedVehicles = data.allUsedVehicleCatfish
      ? data.allUsedVehicleCatfish.edges.map(({ node }) => node)
      : []
    const makeModels = data.makeModels.edges.map(({ node }) => node)
    const locations = data.dropdownLocations.edges.map(({ node }) => node)
    const { make, model, submodels, offers } = pageContext
    const normaliseModelForVehicleCard = createModelNormaliser(make, makeModels)
    const ctaListItems = [
      {
        label: 'View Offers',
        to: `/${model.category}s/offers#${normaliseMake(make.name)}`
      },
      {
        label: 'Register Interest',
        onClick: this.handleTestDriveEnqiryToggle
      }
    ]
    if (model.brochureUrl)
      ctaListItems.push({ label: 'Download brochure', href: model.brochureUrl })
    const metaTitle = `${make.name} ${model.name}`
    const metaDescription = htmlStripper(model.description)
    const offerSlides =
      offers &&
      offers
        .map((o) =>
          o.sell
            ? { text: o.sell, link: o.callToActionLink || o.slug }
            : undefined
        )
        .filter((s) => s !== undefined)

    const brands = new Set(
      locations.reduce(
        (acc, location) => [...acc, ...location.vehicles.map((v) => v.name)],
        []
      )
    )
    const brandSpecific = brands.size === 1
    const brand = brands.values().next().value

    const locationNames = locations.map((location) => location.location)
    const singleLocation = locations[0]

    const calloutImage = model && model.calloutImages && model.calloutImages[0]
    const calloutLink = model && model.calloutLink
    const calloutApplyToSubModels = model && model.calloutApplyToSubModels

    return (
      <Layout
        location={this.props.location}
        category={make.category}
        highlightNew
      >
        <Helmet
          title={metaTitle}
          meta={createMeta({ ...model, metaTitle, metaDescription })}
        />
        <FlexWrapper flexWrap="wrap">
          {offerSlides && offerSlides.length !== 0 && (
            <ContentBar offer slides={offerSlides} />
          )}
          <ContentBrandHero
            dark
            compact
            title={model.marketingHeader || model.name}
            summary={model.description}
            make={make.name}
            img={model.heroImages[0]}
            bg={model.backgroundImages[0]}
            calloutImage={calloutImage}
            calloutLink={calloutLink}
          />

          <ContentBrandCtaBar joined items={ctaListItems} removemargin offset />
          <ContentBrandModels
            models={submodels.map(normaliseModelForVehicleCard)}
            make={make.name}
            calloutImage={calloutImage}
            calloutLink={calloutLink}
            calloutApplyToSubModels={calloutApplyToSubModels}
          />
          <ContentModal
            title="Register Interest"
            subtitle="Please fill in your details below"
            open={this.state.isTestDriveEnquiryOpen}
            onClose={this.handleTestDriveEnqiryToggle}
          >
            <ModalSection width={'100%'} py={20} px={'7%'}>
              <FormTestDrive
                locations={locationNames}
                newVehicle
                vehicleId={model._id}
                brand={brand}
                brandSpecific={brandSpecific}
                location={singleLocation}
              />
            </ModalSection>
          </ContentModal>
        </FlexWrapper>
        {usedVehicles.length > 0 && (
          <UsedVehicleSelection
            make={make.brandName || make.name}
            model={model.name}
            usedVehicles={usedVehicles.map(normaliseUsedVehicleForVehicleCard)}
          />
        )}
      </Layout>
    )
  }
}

export default Model

export const pageQuery = graphql`
  fragment usedVehicleSummaryFields on UsedVehicleCatfish {
    brand
    category
    derivative
    fuel
    image
    mileage
    model
    registration
    transmissionStyle
    value
  }

  fragment makeSocialLinks on MakeCatfish {
    facebookSocialLink
    instagramSocialLink
  }

  query ModelUsedVehicleQuery($usedModel: String!, $makeId: String!) {
    makeModels: allModelCatfish(filter: { make: { eq: $makeId } }) {
      edges {
        node {
          ...modelVehicleCardFields
        }
      }
    }
    dropdownLocations: allDealershipCatfish(
      filter: {
        disableVehicleAllocating: { eq: false }
        vehicles: { elemMatch: { _id: { eq: $makeId } } }
      }
    ) {
      edges {
        node {
          id: _id
          slug
          shortName
          location
          vehicles {
            name
          }
        }
      }
    }
    make: makeCatfish(id: { eq: $makeId }) {
      ...makeSocialLinks
    }
    allUsedVehicleCatfish(
      filter: { model: { eq: $usedModel }, state: { eq: "Published" } }
      limit: 5
    ) {
      edges {
        ...usedVehicleSummaryWidgetFields
      }
    }
  }
`
